'use client';

import { useState, useRef, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { ChevronDownIcon } from '../icons';
import { Tooltip } from '../molecules/Tooltip';

export interface MultiSelectOption {
  id: number;
  label: string;
  category?: string;
  icon?: ReactNode;
  tooltip?: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
  placeholder?: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
  required?: boolean | { required: boolean; message: string };
  searchable?: boolean;
  showSelectAll?: boolean;
  hideSelectedChips?: boolean;
  onSearch?: (term: string) => void;
  /**
   * IDs that are "marked" (e.g. hidden-by-default / dynamic). Marked chips render
   * grayed-out. Requires `onToggleMarked` to enable the per-chip popover.
   */
  markedIds?: number[];
  /** When provided, hovering a chip body shows a small popover to toggle its marked state. */
  onToggleMarked?: (id: number) => void;
  /** Popover title shown when hovering a chip (e.g. "Dynamic property"). */
  markPopoverTitle?: string;
  /** Popover helper text describing what marking does. */
  markPopoverDescription?: string;
  /** Action label for the toggle when the chip is currently marked (describes the click action, e.g. "Show by default"). */
  markActiveLabel?: string;
  /** Action label for the toggle when the chip is not marked (describes the click action, e.g. "Hide by default"). */
  markInactiveLabel?: string;
}

export const MultiSelect = ({
  options,
  selectedIds,
  onChange,
  placeholder = 'Select items...',
  label,
  description,
  disabled = false,
  isLoading = false,
  emptyMessage = 'No items available.',
  required = false,
  searchable = false,
  showSelectAll = true,
  hideSelectedChips = false,
  onSearch,
  markedIds,
  onToggleMarked,
  markPopoverTitle = 'Hide by default',
  markPopoverDescription,
  markActiveLabel = 'Show by default',
  markInactiveLabel = 'Hide by default',
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredChipId, setHoveredChipId] = useState<number | null>(null);
  const markEnabled = typeof onToggleMarked === 'function';
  const markedSet = useMemo(() => new Set(markedIds ?? []), [markedIds]);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      return;
    }

    searchInputRef.current?.focus();

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const estimatedMenuHeight = 350;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    setOpenUp(spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow);
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    // When onSearch is provided the parent owns filtering (server-side search),
    // so options already reflect the current query. Filtering again locally would
    // hide valid server results and flash "No results found" while a request is
    // in flight — so we render options as-is in that mode.
    if (onSearch) return options;
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(term) ||
      String(opt.id).toLowerCase().includes(term) ||
      (opt.category?.toLowerCase().includes(term) ?? false)
    );
  }, [options, searchTerm, onSearch]);

  const visibleOptionIds = useMemo(() => filteredOptions.map((opt) => opt.id), [filteredOptions]);
  const selectedVisibleCount = useMemo(
    () => visibleOptionIds.filter((id) => selectedIds.includes(id)).length,
    [selectedIds, visibleOptionIds],
  );
  const allVisibleSelected = visibleOptionIds.length > 0 && selectedVisibleCount === visibleOptionIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && selectedVisibleCount < visibleOptionIds.length;
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!showSelectAll) return;
    if (!selectAllCheckboxRef.current) return;
    selectAllCheckboxRef.current.indeterminate = someVisibleSelected;
  }, [showSelectAll, someVisibleSelected]);

  const handleToggleSelectAll = () => {
    if (!showSelectAll) return;
    if (disabled) return;
    if (visibleOptionIds.length === 0) return;

    if (allVisibleSelected) {
      onChange(selectedIds.filter((id) => !visibleOptionIds.includes(id)));
      return;
    }

    const next = new Set(selectedIds);
    visibleOptionIds.forEach((id) => next.add(id));
    onChange(Array.from(next));
  };

  const handleToggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const selectedCount = selectedIds.length;
  const isRequired = typeof required === 'boolean' ? required : required?.required;
  const requiredMessage = typeof required === 'object' ? required.message : 'This field is required';
  const [isInvalid, setIsInvalid] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const handleInvalid = useCallback((e: Event) => {
    setIsInvalid(true);
    e.preventDefault();
  }, []);

  useEffect(() => {
    const hiddenInput = hiddenInputRef.current;
    if (hiddenInput && isRequired) {
      hiddenInput.addEventListener('invalid', handleInvalid);
      return () => hiddenInput.removeEventListener('invalid', handleInvalid);
    }
  }, [isRequired, handleInvalid]);

  // Update hidden input validity
  useEffect(() => {
    if (hiddenInputRef.current && isRequired) {
      if (selectedCount === 0) {
        hiddenInputRef.current.setCustomValidity('This field is required');
      } else {
        hiddenInputRef.current.setCustomValidity('');
        setIsInvalid(false);
      }
    }
  }, [selectedCount, isRequired]);

  return (
    <div
      className="space-y-2"
      ref={containerRef}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && isOpen) {
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          setIsOpen(false);
        }
      }}
    >
      {label && (
        <div>
          <label className="font-roboto text-[14px] text-[#2d3a3f] font-medium text-sm">
            {label}
            {isRequired && <span className="text-[#2d3a3f] ml-1">*</span>}
          </label>
          {description && <p className="text-[12px] text-[#5a717a]">{description}</p>}
        </div>
      )}

      <div className="relative">
        <div
          className={`w-full flex items-center justify-between rounded-md border border-[#d9e0e3] px-2.5 py-1.5 text-[14px] text-[#2d3a3f] transition-colors ${
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-[#079a95]/40'
          } ${isOpen ? 'border-[#079a95] ring-2 ring-[#079a95]/20' : ''}`}
          onClick={() => !disabled && !isOpen && setIsOpen(true)}
        >
          <div className="flex-1 flex items-center text-left min-w-0">
            <input
              ref={searchInputRef}
              type="text"
              value={isOpen && searchable ? searchTerm : ''}
              onChange={(e) => {
                if (!searchable) return;
                setSearchTerm(e.target.value);
                onSearch?.(e.target.value);
              }}
              placeholder={isLoading ? 'Loading...' : selectedCount === 0 ? placeholder : `${selectedCount} selected`}
              className={`w-full bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-[14px] text-[#2d3a3f] placeholder:text-[#97a7ad] p-0 h-auto ${!isOpen || !searchable ? 'cursor-pointer caret-transparent' : ''}`}
              readOnly={!isOpen || !searchable}
              onClick={() => !disabled && setIsOpen(true)}
            />
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) setIsOpen(!isOpen);
            }}
            disabled={disabled}
            className={`text-[#5a717a] transition-transform duration-200 flex-shrink-0 cursor-pointer ${
              isOpen ? 'rotate-180' : ''
            }`}
          >
            <ChevronDownIcon className='w-4 h-4' />
          </button>
        </div>

        {isOpen && (
          <div
            className={`absolute left-0 right-0 z-20 max-h-64 overflow-y-auto rounded-lg border border-[#d9e0e3] bg-white shadow-lg ${
              openUp ? 'bottom-full mb-2' : 'top-full mt-2'
            }`}
          >
            <div className="space-y-1 p-2">
              {isLoading ? (
                <div className="p-2 text-center text-[12px] text-[#5a717a]">
                  Loading…
                </div>
              ) : options.length === 0 ? (
                <div className="p-2 text-[12px] text-[#5a717a]">
                  {emptyMessage}
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="p-2 text-[12px] text-[#5a717a]">
                  No results found
                </div>
              ) : (
                <>
                  {showSelectAll && (
                    <>
                      <label
                        className="flex items-center gap-2 cursor-pointer rounded-lg px-2 py-2 transition-colors hover:bg-[#f5f7f8] group"
                      >
                        <input
                          ref={selectAllCheckboxRef}
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={handleToggleSelectAll}
                          disabled={disabled}
                          className="h-4 w-4 rounded border-[#c9d1d4] text-[#079a95] focus:ring-[#079a95] cursor-pointer"
                        />
                        <div className="flex flex-col flex-1">
                          <span className="font-roboto text-[14px] text-[#2d3a3f] transition-colors group-hover:text-[#079a95]">
                            Select all
                          </span>
                        </div>
                      </label>

                      <div className="h-px bg-[#d9e0e3] my-1" />
                    </>
                  )}

                  {filteredOptions.map((option) => {
                    const isChecked = selectedIds.includes(option.id);
                    return (
                      <label
                        key={option.id}
                        className="flex items-center gap-2 cursor-pointer rounded-lg px-2 py-2 transition-colors hover:bg-[#f5f7f8] group"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggle(option.id)}
                          className="h-4 w-4 rounded border-[#c9d1d4] text-[#079a95] focus:ring-[#079a95] cursor-pointer"
                        />
                        <div className="flex flex-col flex-1">
                          <span className="font-roboto text-[14px] text-[#2d3a3f] transition-colors group-hover:text-[#079a95]">
                            {option.label}
                          </span>
                          {option.category && (
                            <span className="text-[12px] text-[#5a717a]">{option.category}</span>
                          )}
                        </div>
                        {option.icon && (
                          <div className="w-4 h-4 text-[#5a717a] flex-shrink-0">
                            {option.icon}
                          </div>
                        )}
                      </label>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {isRequired && isInvalid && <p className="text-[12px] text-red-500">{requiredMessage}</p>}
      {isRequired && <input ref={hiddenInputRef} type="hidden" required />}

      {!hideSelectedChips && selectedCount > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {options
            .filter((option) => selectedIds.includes(option.id))
            .map((option) => {
              const isMarked = markedSet.has(option.id);
              const showPopover = markEnabled && hoveredChipId === option.id;
              return (
                <div
                  key={option.id}
                  className="relative"
                  onMouseEnter={markEnabled ? () => setHoveredChipId(option.id) : undefined}
                  onMouseLeave={markEnabled ? () => setHoveredChipId(null) : undefined}
                >
                  <div
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-roboto text-[13px] group transition-colors ${
                      isMarked
                        ? 'bg-[#eef1f2] text-[#97a7ad] hover:bg-[#e4e8ea]'
                        : 'bg-[#079a95]/10 text-[#079a95] hover:bg-[#079a95]/20'
                    }`}
                  >
                    {markEnabled && isMarked && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                        <line x1="2" x2="22" y1="2" y2="22" />
                      </svg>
                    )}
                    {option.tooltip ? (
                      <Tooltip content={option.tooltip} position="top">
                        <span className="cursor-default">{option.label}</span>
                      </Tooltip>
                    ) : (
                      <span className={markEnabled ? 'cursor-default' : undefined}>{option.label}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleToggle(option.id)}
                      className={`rounded-full p-0.5 transition-colors ${
                        isMarked ? 'hover:bg-[#97a7ad]/30' : 'hover:bg-[#079a95]/30'
                      }`}
                      aria-label={`Remove ${option.label}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M18 6 6 18"></path>
                        <path d="m6 6 12 12"></path>
                      </svg>
                    </button>
                  </div>

                  {showPopover && (
                    <div className="absolute left-0 top-full z-30 w-56 pt-1.5">
                      <div className="rounded-md border border-[#d9e0e3] bg-white p-3 shadow-lg">
                      <p className="font-roboto text-[13px] font-medium text-[#2d3a3f]">{markPopoverTitle}</p>
                      <p className="mt-1 font-roboto text-[12px] leading-snug text-[#5a717a]">
                        {markPopoverDescription ??
                          'Hidden-by-default metrics are not shown when the widget loads. Viewers can reveal them from the widget’s metrics/columns dropdown.'}
                      </p>
                      <button
                        type="button"
                        onClick={() => onToggleMarked?.(option.id)}
                        className={`mt-2.5 flex w-full items-center justify-center gap-1.5 rounded px-2 py-1.5 font-roboto text-[12px] font-medium transition-colors ${
                          isMarked
                            ? 'bg-[#eef1f2] text-[#2d3a3f] hover:bg-[#e4e8ea]'
                            : 'bg-[#079a95] text-white hover:bg-[#067a76]'
                        }`}
                      >
                        {isMarked ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                            <line x1="2" x2="22" y1="2" y2="22" />
                          </svg>
                        )}
                        {isMarked ? markActiveLabel : markInactiveLabel}
                      </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};
